import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { StatusBadge } from '@/components/ui/Badge';
import { TableRowSkeleton } from '@/components/ui/Skeleton';

interface Account {
  id: number;
  alias: string;
  customer_id: string;
  name: string;
  currency: string;
  status: string;
  vertical: string;
  configured: boolean;
  target_roas: number | string | null;
  target_cpa: number | string | null;
  monthly_budget: number | string | null;
  owner_specialist?: { id: number; name: string } | null;
  last_sync_at: string | null;
  last_sync_status: string | null;
}

const verticalLabels: Record<string, string> = {
  services_local: 'Usługi lokalne',
  services_b2b: 'Usługi B2B',
  ecommerce: 'E-commerce',
  mixed: 'Mix',
};

export function AccountsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data: accounts = [], isLoading } = useQuery<Account[]>({
    queryKey: ['accounts'],
    queryFn: async () => (await api.get('/accounts')).data.data,
  });

  const filtered = accounts.filter((a) => {
    const matchesSearch =
      !search ||
      a.name.toLowerCase().includes(search.toLowerCase()) ||
      a.alias.toLowerCase().includes(search.toLowerCase()) ||
      a.customer_id.includes(search);
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">{t('accounts.title')}</h1>
        <button className="btn-primary flex items-center gap-2" onClick={() => navigate('/accounts/new')}>
          <Plus size={16} />
          {t('accounts.add')}
        </button>
      </div>

      {/* Filters bar */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            className="input pl-9"
            placeholder="Szukaj po nazwie, aliasie lub ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="select w-40" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">Wszystkie statusy</option>
          <option value="active">Aktywne</option>
          <option value="paused">Wstrzymane</option>
          <option value="archived">Zarchiwizowane</option>
        </select>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <th className="p-4 font-medium">{t('accounts.alias')}</th>
              <th className="p-4 font-medium">{t('accounts.name')}</th>
              <th className="p-4 font-medium">Customer ID</th>
              <th className="p-4 font-medium">Branża</th>
              <th className="p-4 font-medium">Cel</th>
              <th className="p-4 font-medium">{t('accounts.owner')}</th>
              <th className="p-4 font-medium">{t('accounts.status')}</th>
              <th className="p-4 font-medium">{t('accounts.last_sync')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} cols={8} />)}

            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="p-12 text-center text-slate-500">
                  {search || statusFilter !== 'all'
                    ? 'Brak kont pasujących do filtrów.'
                    : 'Brak podłączonych kont. Dodaj pierwsze konto Google Ads.'}
                </td>
              </tr>
            )}

            {filtered.map((a) => (
              <tr
                key={a.id}
                className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 cursor-pointer transition"
                onClick={() => navigate(`/accounts/${a.id}`)}
              >
                <td className="p-4">
                  <span className="font-mono text-xs bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                    {a.alias}
                  </span>
                </td>
                <td className="p-4 font-medium">{a.name}</td>
                <td className="p-4 text-slate-500 font-mono text-xs">
                  {a.customer_id.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}
                </td>
                <td className="p-4 text-slate-500">{verticalLabels[a.vertical] ?? a.vertical}</td>
                <td className="p-4 text-slate-500 text-xs">
                  {a.target_roas ? `ROAS ${Math.round(Number(a.target_roas) * 100)}%` : ''}
                  {a.target_cpa ? `CPA ${Number(a.target_cpa)}` : ''}
                  {!a.target_roas && !a.target_cpa ? '—' : ''}
                </td>
                <td className="p-4">{a.owner_specialist?.name ?? '—'}</td>
                <td className="p-4">
                  <StatusBadge status={a.status} />
                </td>
                <td className="p-4 text-slate-500 text-xs">
                  {a.last_sync_at ? new Date(a.last_sync_at).toLocaleString('pl-PL') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      {!isLoading && (
        <p className="text-xs text-slate-500 mt-3">
          {filtered.length} z {accounts.length} kont
        </p>
      )}
    </div>
  );
}
